import { useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';

import SectionCard from '@/components/common/SectionCard/SectionCard';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { useColumns } from './columns';
import Pagination from '@/components/common/Pagination/Pagination';
import { useGetUploadedFiles } from './hooks/useGetUploadedFiles';
import { BULK_CL_UPDATE_FILES_PER_PAGE } from '@/config/common.config';
import { browserHistory } from '@/routing/browserHistory';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import AgGridLoadingOverlay
  from '@/components/common/TableGrid/CustomComponents/AgGridLoadingOverlay/AgGridLoadingOverlay';
import { BulkClUpdateResponse } from './abstractions/BuldClUpdate.interface';

export default function FileUploadHistoryContainer() {
  const gridRef = useRef<AgGridReact>(null);
  const columns = useColumns();
  const { getLinkWithQuery, getValue } = useAddQueryToUrl();
  const currentPage = parseInt(getValue('page') ?? '1', 10);

  const updateDataIfCurrentPageIsIncorrect = (response: BulkClUpdateResponse) => {
    if (response && response.TotalPages < currentPage && response.TotalPages >= 1) {
      const routePath = getLinkWithQuery({ page: response.TotalPages.toString() });
      browserHistory.push(routePath);
    }
  };

  const { files, isLoading, totalPages, getUploadedFiles } =
    useGetUploadedFiles(updateDataIfCurrentPageIsIncorrect);

  const onClickPage = (page: number) => {
    const routePath = getLinkWithQuery({ page: page.toString() });
    browserHistory.push(routePath);
  };

  useEffect(() => {
    const setOverlay = () => {
      if (isLoading) {
        return gridRef.current?.api?.showLoadingOverlay();
      }

      if (!files?.length) {
        return gridRef.current?.api?.showNoRowsOverlay();
      }

      return gridRef.current?.api?.hideOverlay();
    };

    setOverlay();
  }, [isLoading, files]);

  useEffect(() => {
    getUploadedFiles(currentPage, BULK_CL_UPDATE_FILES_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <SectionCard
      className="mt-20"
      title={{ titleText: 'File Upload History' }}
      padding={true}
    >
      <TableGrid
        ref={gridRef}
        columnDefs={columns}
        rowData={files}
        suppressMovableColumns={true}
        loadingOverlayComponent={AgGridLoadingOverlay}
        defaultColDef={{ sortable: false, resizable: false }}
      />

      <div className="mt-10">
        <div className="d-flex justify-content-end">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            disabled={isLoading}
            onClickPage={onClickPage}
          />
        </div>
      </div>
    </SectionCard>
  );
}
